import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAddPreferredPharmacyMutation } from "src/api/services/preferredPharmacyApi";
import { EPrescribePharmacy, PatientPreferredPharmacy } from "src/api/types";
import PharmacyAutocomplete from "src/components/PharmacyAutocomplete";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import EditModal from "src/shared/components/EditModal";
import { formattedPharmacyAddress } from "src/utils/formatPharmacyAddress";

interface Props {
  open: boolean;
  onClose: () => void;
  patientPharmacy: PatientPreferredPharmacy;
}

const UpdatePreferredPharmacyModal = ({
  onClose,
  open,
  patientPharmacy,
}: Props) => {
  const [updatePharmacy] = useAddPreferredPharmacyMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPharmacy, setSelectedPharmacy] =
    useState<EPrescribePharmacy | null>(null);
  const { t } = useTranslation();
  const component = "preferred-pharmacy.update-modal";
  const errorLog = useSentryLogger("error");

  const handleSubmit = useCallback(async () => {
    if (selectedPharmacy == null) return null;

    setLoading(true);

    try {
      const pharmacy = await updatePharmacy({
        external_pharmacy_id: String(selectedPharmacy.PharmacyId),
        name: selectedPharmacy.StoreName,
        full_address: formattedPharmacyAddress(selectedPharmacy),
      }).unwrap();

      if (pharmacy != null) {
        onClose();
      }
    } catch (err) {
      errorLog("Could not update preferred pharmacy", err);
    } finally {
      setLoading(false);
    }
  }, [errorLog, onClose, selectedPharmacy, updatePharmacy]);

  return (
    <EditModal
      open={open}
      onClose={onClose}
      loading={loading}
      title={t(`${component}.title`, "Pharmacy Address")}
      subTitle={t(`${component}.subtext`, "Manage your pharmacy address")}
      onSubmit={handleSubmit}
    >
      <PharmacyAutocomplete
        patientPharmacy={patientPharmacy}
        selectedPharmacy={selectedPharmacy}
        setSelectedPharmacy={setSelectedPharmacy}
      />
    </EditModal>
  );
};

export default UpdatePreferredPharmacyModal;
